/**
 * MathML <-> inline TeX bridging for the question editor.
 *
 * Why: the question editor uses the prebuilt @ckeditor/ckeditor5-build-classic,
 * whose schema silently strips every element it does not recognize and keeps only
 * leaf text nodes. MathML (<math><mfrac>...</mfrac></math>) therefore collapses to
 * garbage like "C2" (and leaks the invisible U+2061 function-application char) the
 * moment a MathML-authored question is loaded for editing — and saving destroys it.
 *
 * Fix (load): before handing content to CKEditor, convert each <math> subtree to a
 * LaTeX string wrapped in `\( ... \)`. That delimiter is plain text, so it survives
 * CKEditor round-trips, and the editor's live KaTeX preview renders it.
 *
 * Fix (save): the DB canonical format is MathML — iteach quiz views, the ilearn
 * online test, PDF generation and the Java stack all render <math> natively but do
 * NOT typeset raw `\(...\)`. So on the way back out we convert the editor's inline
 * TeX to MathML via `restoreMathToStorage`: for formulas the teacher never touched
 * we splice back the byte-identical original MathML (captured on load), and for new
 * or edited formulas we generate fresh MathML with KaTeX. This keeps a no-op edit
 * from silently re-encoding a question, and guarantees every viewer keeps rendering.
 */
import katex from 'katex';

/* Invisible MathML/Unicode operators that must be dropped, not rendered. */
const INVISIBLE_CHARS = /[⁡⁢⁣⁤]/g; // function application, invisible times/separator/plus

/* Greek letters and common symbols -> TeX macros (literal unicode is unreliable in MathJax TeX input). */
const SYMBOL_MAP: Record<string, string> = {
  'α': '\\alpha', 'β': '\\beta', 'γ': '\\gamma', 'δ': '\\delta', 'ε': '\\epsilon',
  'ζ': '\\zeta', 'η': '\\eta', 'θ': '\\theta', 'ι': '\\iota', 'κ': '\\kappa',
  'λ': '\\lambda', 'μ': '\\mu', 'ν': '\\nu', 'ξ': '\\xi', 'π': '\\pi', 'ρ': '\\rho',
  'σ': '\\sigma', 'τ': '\\tau', 'υ': '\\upsilon', 'φ': '\\phi', 'χ': '\\chi',
  'ψ': '\\psi', 'ω': '\\omega',
  'Γ': '\\Gamma', 'Δ': '\\Delta', 'Θ': '\\Theta', 'Λ': '\\Lambda', 'Ξ': '\\Xi',
  'Π': '\\Pi', 'Σ': '\\Sigma', 'Φ': '\\Phi', 'Ψ': '\\Psi', 'Ω': '\\Omega',
  '×': '\\times', '÷': '\\div', '±': '\\pm', '∓': '\\mp', '∗': '*',
  '⋅': '\\cdot', '·': '\\cdot', '∘': '\\circ',
  '≠': '\\neq', '≤': '\\leq', '≥': '\\geq', '≈': '\\approx', '≡': '\\equiv',
  '→': '\\to', '⇒': '\\Rightarrow', '⇔': '\\Leftrightarrow', '∞': '\\infty',
  '∈': '\\in', '∉': '\\notin', '⊂': '\\subset', '⊆': '\\subseteq',
  '∪': '\\cup', '∩': '\\cap', '∅': '\\emptyset', '∀': '\\forall', '∃': '\\exists',
  '∑': '\\sum', '∏': '\\prod', '∫': '\\int', '∂': '\\partial', '∇': '\\nabla',
  '√': '\\sqrt', '∝': '\\propto', '∠': '\\angle', '°': '^{\\circ}',
  '−': '-', '–': '-', // U+2212 minus, en-dash -> hyphen-minus
  '′': "'", '″': "''",
};

/* Multi-character function names that need an upright TeX macro (\sin not s·i·n). */
const FUNCTION_NAMES = new Set([
  'sin', 'cos', 'tan', 'cot', 'sec', 'csc',
  'sinh', 'cosh', 'tanh', 'coth',
  'arcsin', 'arccos', 'arctan',
  'log', 'ln', 'lg', 'exp', 'lim', 'max', 'min', 'gcd', 'det', 'dim',
]);

/** Map a raw text token (mi/mn/mo content) to TeX, char by char for symbols. */
function mapToken(raw: string): string {
  const text = raw.replace(INVISIBLE_CHARS, '').trim();
  if (!text) return '';
  if (SYMBOL_MAP[text]) return SYMBOL_MAP[text];
  if (FUNCTION_NAMES.has(text.toLowerCase())) return '\\' + text.toLowerCase();
  // Per-character substitution for runs that mix plain chars and symbols.
  let out = '';
  for (const ch of text) out += SYMBOL_MAP[ch] || ch;
  return out;
}

/** Wrap a sub-expression in braces for TeX grouping (always safe). */
function group(s: string): string {
  return `{${s}}`;
}

function childrenToTex(node: Element): string {
  let out = '';
  node.childNodes.forEach((child) => {
    out += nodeToTex(child);
  });
  return out;
}

/** Convert an array of MathML element children to their TeX strings (skipping whitespace text). */
function elementChildren(node: Element): Element[] {
  return Array.from(node.children);
}

function nodeToTex(node: Node): string {
  if (node.nodeType === Node.TEXT_NODE) {
    return mapToken(node.textContent || '');
  }
  if (node.nodeType !== Node.ELEMENT_NODE) return '';

  const el = node as Element;
  const tag = el.tagName.toLowerCase();
  const kids = elementChildren(el);

  switch (tag) {
    case 'math':
    case 'mrow':
    case 'mstyle':
    case 'mpadded':
    case 'menclose':
    case 'semantics':
      return childrenToTex(el);

    case 'mi':
    case 'mn':
    case 'mo':
    case 'mtext':
      return mapToken(el.textContent || '');

    case 'mspace':
      return '\\,';

    case 'mfrac':
      if (kids.length >= 2) {
        return `\\frac${group(nodeToTex(kids[0]))}${group(nodeToTex(kids[1]))}`;
      }
      return childrenToTex(el);

    case 'msup':
      if (kids.length >= 2) {
        return `${group(nodeToTex(kids[0]))}^${group(nodeToTex(kids[1]))}`;
      }
      return childrenToTex(el);

    case 'msub':
      if (kids.length >= 2) {
        return `${group(nodeToTex(kids[0]))}_${group(nodeToTex(kids[1]))}`;
      }
      return childrenToTex(el);

    case 'msubsup':
      if (kids.length >= 3) {
        return `${group(nodeToTex(kids[0]))}_${group(nodeToTex(kids[1]))}^${group(nodeToTex(kids[2]))}`;
      }
      return childrenToTex(el);

    case 'munder':
      if (kids.length >= 2) {
        return `${nodeToTex(kids[0])}_${group(nodeToTex(kids[1]))}`;
      }
      return childrenToTex(el);

    case 'mover':
      if (kids.length >= 2) {
        return `${nodeToTex(kids[0])}^${group(nodeToTex(kids[1]))}`;
      }
      return childrenToTex(el);

    case 'munderover':
      if (kids.length >= 3) {
        return `${nodeToTex(kids[0])}_${group(nodeToTex(kids[1]))}^${group(nodeToTex(kids[2]))}`;
      }
      return childrenToTex(el);

    case 'msqrt':
      return `\\sqrt${group(childrenToTex(el))}`;

    case 'mroot':
      if (kids.length >= 2) {
        return `\\sqrt[${nodeToTex(kids[1])}]${group(nodeToTex(kids[0]))}`;
      }
      return `\\sqrt${group(childrenToTex(el))}`;

    case 'mfenced': {
      const open = el.getAttribute('open') ?? '(';
      const close = el.getAttribute('close') ?? ')';
      const sep = el.getAttribute('separators') ?? ',';
      const inner = kids.map((k) => nodeToTex(k)).join(sep);
      return `\\left${open || '.'}${inner}\\right${close || '.'}`;
    }

    case 'annotation':
      return ''; // ignore non-presentation annotations (e.g. original TeX/Content-MathML)

    default:
      return childrenToTex(el);
  }
}

/** Normalize a TeX string the same way `normalizeMathToEditable` does, so the
 * restore map keys line up regardless of incidental whitespace differences. */
function texKey(tex: string): string {
  return tex.replace(/\s+/g, ' ').trim();
}

/**
 * Convert any MathML in an HTML string to inline TeX (`\( ... \)`) and strip stray
 * invisible math characters. Existing TeX (`\(...\)` / math-tex spans) is left intact.
 * Never throws: on any failure the original HTML is returned unchanged.
 *
 * When `restoreMap` is supplied, each converted formula's original MathML outerHTML is
 * recorded under its TeX key so `restoreMathToStorage` can splice it back verbatim on
 * save (a no-op edit must not re-encode the formula).
 */
export function normalizeMathToEditable(html: string, restoreMap?: Map<string, string>): string {
  if (!html) return html;
  // Fast path: nothing math-related to do.
  if (!/<math[\s>]/i.test(html) && !INVISIBLE_CHARS.test(html)) {
    INVISIBLE_CHARS.lastIndex = 0;
    return html;
  }
  INVISIBLE_CHARS.lastIndex = 0;

  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const mathEls = Array.from(doc.querySelectorAll('math'));
    for (const mathEl of mathEls) {
      const originalMathml = mathEl.outerHTML;
      let tex = '';
      try {
        tex = childrenToTex(mathEl).replace(/\s+/g, ' ').trim();
      } catch {
        continue; // leave this node untouched on conversion failure
      }
      if (!tex) {
        mathEl.remove();
        continue;
      }
      if (restoreMap) restoreMap.set(texKey(tex), originalMathml);
      mathEl.replaceWith(doc.createTextNode(`\\(${tex}\\)`));
    }
    // Drop any invisible chars that lived outside <math> elements.
    let out = doc.body.innerHTML;
    out = out.replace(INVISIBLE_CHARS, '');
    return out;
  } catch {
    return html.replace(INVISIBLE_CHARS, '');
  }
}

/** Render a TeX string to a bare `<math>...</math>` MathML string via KaTeX.
 * Returns null when KaTeX cannot parse it (caller then keeps the TeX untouched). */
function texToMathml(tex: string, display: boolean): string | null {
  try {
    const out = katex.renderToString(tex, {
      output: 'mathml',
      displayMode: display,
      throwOnError: true,
    });
    const mathEl = new DOMParser().parseFromString(out, 'text/html').querySelector('math');
    return mathEl ? mathEl.outerHTML : null;
  } catch {
    return null;
  }
}

/**
 * Inverse of `normalizeMathToEditable`: convert the editor's inline TeX
 * (`\(...\)` / `\[...\]`, including inside `<span class="math-tex">` wrappers) back to
 * MathML so the stored value stays in the DB's canonical MathML format.
 *
 * Untouched formulas (their TeX still matches a `restoreMap` key from load time) are
 * restored to their byte-identical original MathML; new or edited formulas are rendered
 * to fresh MathML with KaTeX. Anything KaTeX cannot parse is left as TeX rather than
 * lost. Content with no inline TeX is returned unchanged. Never throws.
 */
export function restoreMathToStorage(html: string, restoreMap?: Map<string, string>): string {
  if (!html || (!html.includes('\\(') && !html.includes('\\['))) return html;
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    // Collect text nodes first — replacing during the walk invalidates the walker.
    const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT);
    const textNodes: Text[] = [];
    while (walker.nextNode()) textNodes.push(walker.currentNode as Text);

    // Inline \(...\) or display \[...\], non-greedy so adjacent formulas stay separate.
    const re = /\\\(([\s\S]+?)\\\)|\\\[([\s\S]+?)\\\]/g;
    for (const tn of textNodes) {
      const text = tn.textContent || '';
      if (!text.includes('\\(') && !text.includes('\\[')) continue;

      const frag = doc.createDocumentFragment();
      let last = 0;
      let m: RegExpExecArray | null;
      re.lastIndex = 0;
      while ((m = re.exec(text)) !== null) {
        if (m.index > last) frag.appendChild(doc.createTextNode(text.slice(last, m.index)));
        const display = m[2] != null;
        const rawTex = (m[1] ?? m[2] ?? '').trim();
        const restored = restoreMap?.get(texKey(rawTex)) ?? texToMathml(rawTex, display);
        if (restored) {
          const tmp = doc.createElement('div');
          tmp.innerHTML = restored;
          while (tmp.firstChild) frag.appendChild(tmp.firstChild);
        } else {
          frag.appendChild(doc.createTextNode(m[0])); // unparseable — keep the TeX
        }
        last = re.lastIndex;
      }
      if (last < text.length) frag.appendChild(doc.createTextNode(text.slice(last)));
      tn.replaceWith(frag);
    }
    return doc.body.innerHTML;
  } catch {
    return html;
  }
}
